import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, graphProductToCard } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CompareProductCard from "@/components/CompareProductCard";
import SearchBox from "@/components/SearchBox";
import { useLocale } from "@/context/LocaleContext";
import { SHOP_CATEGORIES, SHOP_TREE, productMatchesCategory, productMatchesQuery, productFamily, queryLooksApparel } from "@/constants/shop";
import { relatedSearches, storeLabel } from "@/constants/search";
import { ensureShopCategories, sectionsFromTree, storeSlug, subcategoryFor } from "@/constants/catalogCategories";
import EarnKaroCard from "@/components/EarnKaroCard";
import IndiaStorefront, { indiaCatalogQuery } from "@/components/IndiaStorefront";
import { ShopBanners, ShopSidebar, marketBanners } from "@/components/ShopChrome";

function priceOf(product) {
  const raw = product?.offers?.[0]?.price ?? product?.price;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

function sortProducts(list, sort) {
  if (sort !== "price_asc" && sort !== "price_desc") return list;
  return [...list].sort((left, right) => {
    const a = priceOf(left);
    const b = priceOf(right);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return sort === "price_asc" ? a - b : b - a;
  });
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const sub = params.get("sub") || "";
  const storeParam = params.get("store") || "";
  const search = params.get("q") || "";
  const brandId = params.get("brand") || "";
  const indiaCategory = params.get("ik") || "";
  const dept = params.get("dept") || "";
  const shelf = params.get("shelf") || "";
  const graphCategory = SHOP_CATEGORIES.includes(category) ? category : "All";
  const sort = params.get("sort") === "price_desc" ? "price_desc" : params.get("sort") === "price_asc" ? "price_asc" : "";
  const [q, setQ] = useState(search);
  const [more, setMore] = useState([]);
  const [pageToken, setPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const { country, currency, setCountry, countries } = useLocale();
  const [storeFilter, setStoreFilter] = useState("");

  useEffect(() => {
    setQ(search);
    setStoreFilter("");
  }, [search]);

  const { data: brandData } = useQuery({
    queryKey: ["channel3-brands", category],
    queryFn: async () => {
      const { data: res } = await api.get("/graph/external/brands", {
        params: category !== "All" ? { category } : {},
      });
      return res;
    },
    staleTime: 10 * 60 * 1000,
  });
  const brands = useMemo(() => brandData?.brands || [], [brandData]);

  useEffect(() => {
    if (!brandId || !brands.length) return;
    if (brands.some((b) => b.id === brandId)) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("brand");
      return next;
    }, { replace: true });
  }, [brandId, brands, setParams]);

  const india = (country || "").toUpperCase() === "IN";
  const indiaHome = india && !search && !brandId;
  const catalogCategory = category === "All" ? "" : category;
  const { data: indiaData } = useQuery({
    ...indiaCatalogQuery(dept, shelf, sort, catalogCategory, sub, storeParam),
    enabled: india,
  });
  const { data: categoryTree } = useQuery({
    queryKey: ["catalog-categories", country],
    queryFn: async () => (await api.get("/catalog/categories", { params: { country } })).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["graph-search", graphCategory, search, brandId, country, currency, indiaCategory],
    enabled: !indiaHome,
    staleTime: 20_000,
    queryFn: async () => {
      const filters = { country, currency };
      if (graphCategory !== "All") filters.category = graphCategory;
      if (brandId) filters.brand_id = brandId;
      if ((country || "").toUpperCase() === "IN" && indiaCategory) filters.earnkaro_category = indiaCategory;
      const { data: res } = await api.post("/graph/search", {
        query: search,
        filters,
        limit: 42,
      });
      if (search) {
        api.post("/graph/events", { type: "impression", query: search, publisher: "aura-shop" }).catch(() => {});
      }
      return res;
    },
  });

  useEffect(() => {
    setMore([]);
    setPageToken(data?.next_page_token || null);
  }, [category, search, brandId, country, currency, indiaCategory, data?.next_page_token]);

  const rawProducts = [...(data?.products || []).map(graphProductToCard), ...more].filter((p) => p.id);
  const filtered = rawProducts.filter((p) => {
    if (graphCategory !== "All" && p.source !== "earnkaro" && !productMatchesCategory(p, graphCategory)) return false;
    if (catalogCategory && graphCategory === "All" && String(p.category || "").toLowerCase() !== catalogCategory) return false;
    if (sub) {
      const got = p.subcategory || subcategoryFor(p.title, p.category, categoryTree?.rules, categoryTree?.families);
      if (got !== sub) return false;
    }
    if (storeParam && storeSlug(p.store || storeLabel(p)) !== storeParam) return false;
    if (search && !productMatchesQuery(p, search) && productFamily(p) === "apparel") return false;
    if (search && !queryLooksApparel(search) && productFamily(p) === "apparel") return false;
    if (brandId) {
      const selected = brands.find((b) => b.id === brandId);
      const name = (selected?.name || "").toLowerCase();
      const productBrand = String(p.brand || "").toLowerCase();
      const hay = `${p.brand || ""} ${p.title || ""}`.toLowerCase();
      const sameId = p.brand_id && p.brand_id === brandId;
      const sameName = name && productBrand === name;
      const inTitle = name.length > 1 && hay.includes(name);
      const otherId = p.brand_id && p.brand_id !== brandId;
      const otherName = name && productBrand && productBrand !== name;
      // Channel3 accessory rows often omit brand; the API already scoped by brand_ids.
      if ((otherId || otherName) && !sameId && !sameName && !inTitle) return false;
    }
    return true;
  });
  const showBrands = !search || queryLooksApparel(search) || category !== "All";
  const stores = [...new Set(filtered.map((p) => storeLabel(p)).filter(Boolean))].slice(0, 12);
  const products = sortProducts(filtered.filter((p) => !storeFilter || storeLabel(p) === storeFilter), sort);
  const related = search ? relatedSearches(search) : [];
  const comparing = Boolean(search);
  const selectedBrand = brands.find((b) => b.id === brandId);

  const setDept = (id, shelfId = "") => {
    const next = new URLSearchParams(params);
    if (id) next.set("dept", id);
    else next.delete("dept");
    if (shelfId) next.set("shelf", shelfId);
    else next.delete("shelf");
    next.delete("q");
    next.delete("category");
    next.delete("sub");
    next.delete("store");
    next.delete("brand");
    next.delete("ik");
    setQ("");
    setParams(next, { replace: true });
  };

  const chooseCountry = (code) => {
    const nextCode = (code || "US").toUpperCase();
    setCountry(nextCode);
    const next = new URLSearchParams(params);
    next.delete("category");
    next.delete("sub");
    next.delete("store");
    next.delete("brand");
    if (nextCode === "IN") {
      next.delete("brand");
    } else {
      next.delete("dept");
      next.delete("shelf");
      next.delete("ik");
    }
    setParams(next, { replace: true });
  };

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category");
    else next.set("category", c);
    next.delete("brand");
    next.delete("dept");
    next.delete("shelf");
    next.delete("sub");
    setParams(next, { replace: true });
  };

  const setCatalog = (categoryId, subId = "") => {
    const next = new URLSearchParams(params);
    if (categoryId && categoryId !== "All") next.set("category", categoryId);
    else next.delete("category");
    if (subId) next.set("sub", subId);
    else next.delete("sub");
    next.delete("dept");
    next.delete("shelf");
    next.delete("ik");
    next.delete("brand");
    next.delete("q");
    setQ("");
    setParams(next, { replace: true });
  };

  const setStore = (id) => {
    const next = new URLSearchParams(params);
    if (id && id !== storeParam) next.set("store", id);
    else next.delete("store");
    setParams(next, { replace: true });
  };

  const openBanner = (banner) => {
    if (india) setDept(banner.department || banner.id || "");
    else if (banner.category) setCategory(banner.category);
  };

  const setBrand = (id) => {
    const next = new URLSearchParams(params);
    if (!id) {
      next.delete("brand");
    } else {
      next.set("brand", id);
      const selected = brands.find((b) => b.id === id);
      if (selected?.name) next.set("q", selected.name);
    }
    setParams(next, { replace: true });
  };

  const setSort = (value) => {
    const next = new URLSearchParams(params);
    if (value === "price_asc" || value === "price_desc") next.set("sort", value);
    else next.delete("sort");
    setParams(next, { replace: true });
  };

  const submitSearch = (nextQ) => {
    const next = new URLSearchParams(params);
    const term = String(nextQ ?? q).trim();
    if (term) next.set("q", term);
    else next.delete("q");
    setStoreFilter("");
    setParams(next, { replace: true });
  };

  const loadMore = async () => {
    if (!pageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const filters = { country, currency };
      if (graphCategory !== "All") filters.category = graphCategory;
      if (brandId) filters.brand_id = brandId;
      if ((country || "").toUpperCase() === "IN" && indiaCategory) filters.earnkaro_category = indiaCategory;
      const { data: res } = await api.post("/graph/search", {
        query: search,
        filters,
        limit: 30,
        page_token: pageToken,
      });
      const extra = (res?.products || []).map(graphProductToCard);
      setMore((prev) => [...prev, ...extra]);
      setPageToken(res?.next_page_token || null);
    } finally {
      setLoadingMore(false);
    }
  };

  const treeSections = sectionsFromTree(categoryTree);
  const baseSections = treeSections.length
    ? treeSections
    : (india
      ? (indiaData?.departments || []).map((row) => ({
          id: row.id,
          label: row.label,
          count: row.count,
          kind: "department",
          children: row.children || [],
        }))
      : SHOP_TREE);
  const menuSections = [
    { id: "all", label: india ? "All departments" : "All categories" },
    ...ensureShopCategories(baseSections, country),
  ];
  const located = (() => {
    for (const section of menuSections) {
      if (section.kind === "category" && section.id === catalogCategory) {
        return { activeId: section.id, activeChild: sub, activeGrand: "" };
      }
      for (const child of section.children || []) {
        if (child.id === catalogCategory) {
          return { activeId: section.id, activeChild: child.id, activeGrand: sub };
        }
      }
    }
    if (!catalogCategory) {
      return { activeId: india ? (dept || "all") : "all", activeChild: india ? shelf : "", activeGrand: "" };
    }
    return { activeId: "", activeChild: catalogCategory, activeGrand: sub };
  })();
  const pickSection = (id) => {
    if (id === "all") {
      setCatalog("");
      return;
    }
    const section = menuSections.find((row) => row.id === id);
    if (!section) return;
    if (section.kind === "category") {
      setCatalog(section.id, "");
      return;
    }
    if (section.children?.length === 1 && !(section.children[0].children || []).length) {
      setCatalog(section.children[0].id, "");
      return;
    }
    const named = section.children?.find((child) => child.label === section.label);
    if (named) setCatalog(named.id, "");
  };
  const pickChild = (parent, child) => {
    const section = menuSections.find((row) => row.id === parent);
    if (section?.kind === "category") setCatalog(parent, child);
    else setCatalog(child, "");
  };
  const banners = india ? (indiaData?.banners || []) : marketBanners();
  const selectedLabel = (() => {
    for (const section of menuSections) {
      if (section.id === catalogCategory) return section.label;
      for (const child of section.children || []) {
        if (child.id === catalogCategory) return child.label;
      }
    }
    return "";
  })();
  const pageTitle = search || selectedBrand?.name || selectedLabel || (india ? (indiaData?.label || "Shop India") : "Shop Shoptosa");

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 pt-24 pb-24">
      {india && !indiaData ? (
        <div className="mb-8 h-64 shimmer" data-testid="shop-banners-loading" />
      ) : (
        <ShopBanners banners={banners} onOpen={openBanner} />
      )}
      <div className="md:flex md:items-start md:gap-8">
        <ShopSidebar
          countries={countries}
          country={country}
          onCountry={chooseCountry}
          sections={menuSections}
          stores={categoryTree?.stores || []}
          activeId={located.activeId}
          activeChild={located.activeChild}
          activeGrand={located.activeGrand}
          activeStore={storeParam}
          onSelect={pickSection}
          onSelectChild={pickChild}
          onSelectGrand={(_parent, child, grand) => setCatalog(child, grand)}
          onSelectStore={setStore}
        />
        <div className="min-w-0 flex-1">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-serif text-3xl tracking-tight">{pageTitle}</h1>
              {india ? (
                <Link to="/deals" className="mt-1 inline-block text-sm text-aura-muted underline" data-testid="shop-offers-deals">
                  Offers & Deals
                </Link>
              ) : null}
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <label className="flex items-center gap-2 text-xs text-aura-muted">
                Sort by
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  data-testid="shop-sort"
                  aria-label="Sort by"
                  className="border border-aura-border bg-transparent px-3 py-2 text-sm text-aura-ink"
                >
                  <option value="">Featured</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </select>
              </label>
              <SearchBox value={q} onChange={setQ} onSearch={submitSearch} testId="shop-search" className="w-full sm:w-80" />
            </div>
          </div>
      {indiaHome ? (
        <IndiaStorefront
          department={catalogCategory ? "" : dept}
          shelf={catalogCategory ? "" : shelf}
          sort={sort}
          category={catalogCategory}
          sub={sub}
          store={storeParam}
        />
      ) : null}

      {!indiaHome && (
      <>

      {related.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="related-searches">
          {related.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setQ(s);
                submitSearch(s);
              }}
              className={`text-xs px-3 py-1.5 border transition-colors ${
                search.toLowerCase() === s.toLowerCase()
                  ? "border-aura-accent text-aura-accent"
                  : "border-aura-border text-aura-ink/70 hover:text-aura-ink"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {showBrands && brands.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-10" data-testid="brand-chips">
          <button
            onClick={() => setBrand("")}
            className={`text-xs tracking-luxe uppercase px-3 py-1.5 border transition-colors ${
              !brandId ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-ink/60 hover:text-aura-ink"
            }`}
          >
            All brands
          </button>
          {brands.map((b) => (
            <button
              key={b.id || b.name}
              onClick={() => setBrand(b.id)}
              data-testid={`brand-${String(b.name || b.id || "brand").toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs tracking-luxe uppercase px-3 py-1.5 border transition-colors ${
                brandId === b.id ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-ink/60 hover:text-aura-ink"
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}

      {comparing && stores.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="store-filters">
          <button
            type="button"
            onClick={() => setStoreFilter("")}
            className={`text-xs px-3 py-1.5 border ${!storeFilter ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-ink/70"}`}
          >
            All stores
          </button>
          {stores.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStoreFilter(s)}
              className={`text-xs px-3 py-1.5 border ${storeFilter === s ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-ink/70"}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
            <div className="grid grid-cols-2 items-stretch gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-full">
              <div className="aspect-[3/4] shimmer" />
              <div className="h-4 w-2/3 shimmer" />
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="py-24 text-center text-aura-muted">
          {search && data?.external_enabled === false
            ? "No Shoptosa listings matched. Add the external catalog key in Admin to search partner stores."
            : "No listings matched. Try another search."}
        </div>
      ) : (
        <>
          <div
            className="grid grid-cols-2 items-stretch gap-4 sm:grid-cols-3 lg:grid-cols-4"
            data-testid="products-grid"
          >
            {products.map((p, i) => (
              p.source === "earnkaro"
                ? <EarnKaroCard key={p.id} product={p} />
                : comparing
                  ? <CompareProductCard key={p.id} product={p} index={i} instant />
                  : <ProductCard key={p.id} product={p} index={i} instant />
            ))}
          </div>
          {pageToken && (
            <div className="mt-14 text-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="shop-load-more"
                className="text-sm tracking-luxe uppercase border border-aura-border px-8 py-3 hover:border-aura-accent disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : "Load more partner listings"}
              </button>
            </div>
          )}
        </>
      )}
      </>
      )}
        </div>
      </div>
    </div>
  );
}
